import AuthComponent from "@components/auth/component";
import NoRecord from "@components/layouts/NoRecord";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import TableSkeleton from "@components/layouts/TableSkeleton";
import {
  pushNotificationColumnNames,
  pushNotificationColumns,
} from "@helpers/columns/others/push-notification.columns";
import { DeletePopup } from "@helpers/popups/DeletePopup";
import { Toast } from "@helpers/popups/Toast";
import useGetPermission from "@hooks/common/useGetPermission";
import { useDidMountEffect } from "@hooks/useDidMountEffect";
import { useErrorHandling } from "@hooks/useErrorHandling";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import {
  useDeletePushNotificationMutation,
  useGetAllPushNotificationsQuery,
  useSendPushNotificationMutation,
} from "@states/others/push-notification.api";
import { TDeleteSuccesResponse } from "@type-defs/general/TDeleteSuccessResponse";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { PUSH_NOTIFICATION } from "@utils/constants/others/push-notification.constants";
import { castTypeArr } from "@utils/general/cast-type";
import React, { useEffect } from "react";
import DataTable from "react-data-table-component";
import { useLocation } from "react-router-dom";

function PushNotificationPage() {
  const { hasAccess } = useGetPermission();
  const canCreate = hasAccess("create", "Others", "Push Notification");
  const canDelete = hasAccess("delete", "Others", "Push Notification");
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const location = useLocation();
  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetAllPushNotificationsQuery({
    page,
    limit,
    search,
  });
  const [deletePushNotification, { error: deleteError }] =
    useDeletePushNotificationMutation();
  const [sendPushNotification, { error: sendError }] =
    useSendPushNotificationMutation();

  useErrorHandling(
    ...castTypeArr<TFailResponse>([getAllError, deleteError, sendError])
  );

  useDidMountEffect(() => {
    refetch();
  }, [page, limit, search]);

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
  }, [location.state?.reload, refetch]);

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deletePushNotification({ id });
        const successData = "data" in res ? res.data : null;

        if ((successData as TDeleteSuccesResponse)?.success) {
          await Toast.fire({
            icon: "success",
            title: (successData as TDeleteSuccesResponse).message,
          });

          refetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const sendHandler = async (id: number | string) => {
    try {
      const res = await sendPushNotification(id);
      const successData = "data" in res ? (res.data as TSuccessResponse) : null;

      if (successData?.success) {
        await Toast.fire({
          icon: "success",
          title: successData.message,
        });

        refetch();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AuthComponent
      action="view"
      module="Others"
      subModule="Push Notification"
      page
    >
      <React.Fragment>
        <Search setSearch={setSearch} />
        <PageWrapper
          addPath={PUSH_NOTIFICATION.ADD_PATH()}
          addButtonTitle={PUSH_NOTIFICATION.ADD_BUTTON_TITLE}
          title={PUSH_NOTIFICATION.LIST_PAGE_TITLE}
          hasCreatePermission={canCreate}
        >
          <DataTable
            keyField="push_notification_id"
            responsive
            striped
            pagination
            paginationServer
            //@ts-ignore
            paginationTotalRows={data?.paginator?.totalItems}
            progressPending={isFetching}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoRecord />}
            progressComponent={
              <TableSkeleton
                columnNames={pushNotificationColumnNames}
                actionRight
                row={10}
                col={7}
              />
            }
            //@ts-ignore
            columns={pushNotificationColumns(
              deleteHandler,
              sendHandler,
              canDelete,
              canCreate
            )}
            //@ts-ignore
            data={data?.data}
          />
        </PageWrapper>
      </React.Fragment>
    </AuthComponent>
  );
}

export default PushNotificationPage;
